import { tv } from 'tailwind-variants'

import { popupRowStyles } from '@gto/ui/styles/row-styles'

/**
 * Shared row styling for every interactive Menu row: `Item`, `CheckboxItem`,
 * `RadioItem`, and `SubmenuTrigger`. Reflects the design-system spec at
 * https://www.figma.com/design/s5knlq42XjwZx88JuPw8c7/GTOW-Design-System?node-id=6266-88477
 */
export const menuItemVariants = tv({
  base: [
    // Geometry, label and highlight/disabled states are the shared DS popup row —
    // listbox rows (Select, Combobox, Autocomplete) render the identical base.
    // Everything below it is Menu's own: icons, selection and submenu state.
    ...popupRowStyles,
    // Leading icon slot: 16×16. Direct children only (`>`), so the
    // Checkbox/Radio internals of CheckboxItem/RadioItem and icons nested in
    // wrappers keep their own sizing — rows own their bare icon children.
    // To deviate, put `[&>svg]:size-N` on the row's className — a plain
    // `size-N` on the icon itself loses to this rule on specificity.
    '[&>svg]:size-4 [&>svg]:shrink-0',
    // Icon colour per DS spec: action-active by default, primary on highlight,
    // action-disabled-background when disabled. Set directly on `svg` so it
    // wins over `currentColor` inheritance from the row's label colour. The
    // state variants use a single `[&[data-X]_svg]` arbitrary selector — the
    // stacked `data-[X]:[&_svg]:` form does not reliably compile in Tailwind 3.
    '[&_svg]:text-text-action-active',
    // Also covers an open submenu trigger, which retains `data-highlighted`.
    '[&[data-highlighted]_svg]:text-text-primary',
    // Selected = persistent active state (we set `data-selected` from a
    // `selected` prop on Item / SubmenuTrigger). Same tint as highlighted but
    // sticks even when the row isn't focused — useful for a submenu trigger
    // whose child carries the active value.
    'data-[selected]:text-primary-main [&[data-selected]_svg]:text-primary-main',
    // Submenu open: Base UI puts `data-popup-open` on the SubmenuTrigger while
    // its submenu is expanded — tint it so the parent row stays primary even
    // after pointer/keyboard focus moves into the child popup.
    'data-[popup-open]:text-primary-main [&[data-popup-open]_svg]:text-primary-main',
    // Disabled state — wins over highlight AND selection via compound
    // specificity. Tailwind variant ordering is irrelevant here because the
    // compound `[data-disabled][data-X]` selector beats the single-attribute
    // rules above on CSS specificity grounds.
    '[&[data-disabled]_svg]:text-text-action-disabled-background',
    '[&[data-disabled][data-highlighted]_svg]:text-text-action-disabled-background',
    'data-[disabled]:data-[selected]:text-text-disabled [&[data-disabled][data-selected]_svg]:text-text-action-disabled-background',
    'data-[disabled]:data-[popup-open]:text-text-disabled [&[data-disabled][data-popup-open]_svg]:text-text-action-disabled-background',
    'data-[disabled]:data-[checked]:text-text-disabled [&[data-disabled][data-checked]_svg]:text-text-action-disabled-background',
  ],
  variants: {
    // Checked = the row carries the active value in a RadioGroup, or its CheckboxItem is
    // on. Base UI sets `data-checked` on both, but only a RadioItem tints for it: the
    // label colour IS its checked indicator. A CheckboxItem opts out (`none`) — its box
    // already shows the state, and green label + green box read as two indicators. A real
    // variant, not a consumer class-merge override, so the opt-out is visible where the
    // tint is defined (and covers the leading-icon svg rule the override missed).
    checkedTint: {
      label: 'data-[checked]:text-primary-main [&[data-checked]_svg]:text-primary-main',
      none: '',
    },
  },
  defaultVariants: {
    checkedTint: 'label',
  },
})
